// Thin API client. Replaces google.script.run.
// All endpoints return { ok, data, error, message }.
import type { Job } from '@qjob/shared';

const BASE = (import.meta as any).env?.VITE_API_BASE || '';

// Same-origin-relative-to-API proxy for the quotation signature image —
// Drive's public "uc?export=view" hotlink URLs frequently fail to load as
// <img src> in real browsers, so this proxies through our own service
// account instead. See layout-preview.ts's signatureImageSrc opt.
export function getSignatureImageUrl(): string {
  return `${BASE}/api/config/signature-image`;
}

// Same-origin-relative-to-API download proxy for a generated PDF. The server
// sets Content-Disposition: attachment (which forces a download even
// cross-origin, unlike the <a download> attribute), so this URL can be used
// directly as an <a href>. `pdfUrl` is the stored Drive webViewLink (or id).
export function getDocumentDownloadUrl(pdfUrl: string, filename?: string): string {
  const params = new URLSearchParams({ url: String(pdfUrl || '') });
  if (filename) params.set('name', filename);
  return `${BASE}/api/documents/download?${params.toString()}`;
}

export interface ApiResult<T = any> {
  ok: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface GetJobsParams {
  status?: string;
  overdue?: boolean;
  needsReview?: boolean;
  q?: string;
  sheet?: string;
}

export interface SaveJobOptions {
  createQuotation?: boolean;
  createReceipt?: boolean;
  createInvoice?: boolean;
  notifyLine?: boolean;
  skipOverwrite?: boolean;
  replaceReceipt?: boolean;
  receiptDraft?: boolean;
  requireNoDuplicateDocuments?: boolean;
  replaceQuotation?: boolean;
  replaceInvoice?: boolean;
  receiptPaymentId?: string;
  quotationPaymentInstallmentId?: string;
  documentInstallmentId?: string;
  allowScheduleConflict?: boolean;
}

export interface DocumentGenerationResult {
  status: 'synced' | 'skipped' | 'failed';
  message: string;
  number?: string;
  pdfUrl?: string;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 25_000;
// PDF creation includes Browser Rendering plus Drive upload (and can create
// quotation + invoice together), so it legitimately takes longer than a
// normal data request.  The previous 25-second abort fired while the server
// was still working and made the UI report a misleading connection timeout.
const DOCUMENT_REQUEST_TIMEOUT_MS = 180_000;

export class ApiRequestError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function requestTimeoutForOptions(options: SaveJobOptions): number {
  return options.createQuotation || options.createInvoice || options.createReceipt
    ? DOCUMENT_REQUEST_TIMEOUT_MS
    : DEFAULT_REQUEST_TIMEOUT_MS;
}

async function request<T>(path: string, init?: RequestInit, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(BASE + path, {
      ...init,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
      },
    });
    const json: ApiResult<T> = await res.json().catch(() => ({ ok: false, error: 'bad_json' }));
    if (!json.ok) {
      throw new ApiRequestError(
        json.message || json.error || `request failed (${res.status})`,
        json.error || '',
        res.status,
      );
    }
    return json.data as T;
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      throw new Error(
        timeoutMs === DOCUMENT_REQUEST_TIMEOUT_MS
          ? 'สร้างไฟล์ใช้เวลานานเกินกำหนด — กรุณาลองใหม่อีกครั้ง'
          : 'การเชื่อมต่อนานเกินไป — ลองรีเฟรชอีกครั้ง',
      );
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

function withQuery(params: Record<string, any>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'boolean') sp.set(k, v ? '1' : '0');
    else sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? '?' + qs : '';
}

export const api = {
  getHealth: () => request<{ status: string; version: string; time: string }>('/api/health'),

  checkDocumentFiles: (urls: string[]) =>
    request<{ files: { id: string; exists: boolean }[] }>('/api/documents/check', {
      method: 'POST',
      body: JSON.stringify({ urls }),
    }),

  getJobs: (params: GetJobsParams = {}) =>
    request<{ jobs: Job[]; count: number; sheets: string[] }>(
      '/api/jobs' + withQuery(params as any),
    ),

  saveJob: (job: Partial<Job>, options: SaveJobOptions = {}) =>
    request<{
      job: Job;
      rowIndex: number;
      sheetName: string;
      isNew: boolean;
      calendarSync?: { status: string; message: string };
      quotation?: DocumentGenerationResult;
      invoice?: DocumentGenerationResult;
      receipt?: DocumentGenerationResult;
    }>(
      '/api/jobs',
      {
        method: 'POST',
        body: JSON.stringify({ job, options }),
      },
      requestTimeoutForOptions(options),
    ),

  updateJob: (
    rowIndex: number,
    job: Partial<Job>,
    sheetName?: string,
    options: SaveJobOptions = {},
  ) =>
    request<{ job: Job; rowIndex: number; sheetName: string; isNew: boolean }>(
      `/api/jobs/${rowIndex}`,
      {
        method: 'PUT',
        body: JSON.stringify({ job, sheet: sheetName, options }),
      },
      requestTimeoutForOptions(options),
    ),

  deleteJob: (job: Partial<Job>) =>
    request<{ success: boolean; message: string; jobId: string }>('/api/jobs/delete', {
      method: 'POST',
      body: JSON.stringify({
        job: {
          JobID: (job as any)?.JobID,
          _sheetName: (job as any)?._sheetName,
          _rowIndex: (job as any)?._rowIndex,
          Client: (job as any)?.Client,
          'Event Date': (job as any)?.['Event Date'],
          'Item / Job Title': (job as any)?.['Item / Job Title'],
        },
      }),
    }),

  getConfig: () => request<{ config: Record<string, string> }>('/api/config'),

  saveConfig: (config: Record<string, any>) =>
    request<{ updated: string[]; appended: string[] }>('/api/config', {
      method: 'POST',
      body: JSON.stringify({ config }),
    }),

  testLineConnection: () =>
    request<{ message: string }>('/api/config/test-line', { method: 'POST' }),

  testCalendarConnection: () =>
    request<{ message: string }>('/api/config/test-calendar', { method: 'POST' }),

  syncAllJobsToCalendar: () =>
    request<{
      total: number;
      offset: number;
      limit: number;
      processed: number;
      nextOffset: number | null;
      synced: number;
      skipped: number;
      failed: number;
    }>('/api/calendar/sync' + withQuery({ limit: 15, offset: 0 }), { method: 'POST' }),

  syncJobToCalendar: (job: Partial<Job>) =>
    request<{ calendarSync: { status: string; message: string } }>('/api/calendar/sync-job', {
      method: 'POST',
      body: JSON.stringify({ job }),
    }),

  notifyJobLine: (job: Partial<Job>) =>
    request<{ success: boolean; message: string }>('/api/jobs/notify-line', {
      method: 'POST',
      body: JSON.stringify({ job }),
    }),

  deleteCalendarEvent: (calendarId: string, eventId: string, jobId?: string) =>
    request<{ deleted: number }>('/api/calendar/delete-event', {
      method: 'POST',
      body: JSON.stringify({ calendarId, eventId, jobId }),
    }),

  syncJobsToCalendarBatch: (offset: number, limit = 15) =>
    request<{
      total: number;
      offset: number;
      limit: number;
      processed: number;
      nextOffset: number | null;
      synced: number;
      skipped: number;
      failed: number;
    }>('/api/calendar/sync' + withQuery({ limit, offset }), { method: 'POST' }),

  getCalendarSources: () => request<{ sources: { id: string; name: string }[] }>('/api/calendar/sources'),

  getCalendarEvents: (start: string, end: string) =>
    request<{
      events: Array<{
        id: string;
        title: string;
        start: string;
        end: string;
        allDay: boolean;
        location?: string;
        description?: string;
        calendarId: string;
        calendarName: string;
      }>;
    }>('/api/calendar/events' + withQuery({ start, end })),

  uploadSlip: (payload: {
    fileName: string;
    mimeType: string;
    base64: string;
    eventDate?: string;
    jobTitle?: string;
    client?: string;
    jobType?: string;
    folderUrl?: string;
  }) =>
    request<{
      fileUrl: string;
      fileId: string;
      fileName: string;
      downloadUrl?: string;
      thumbnailUrl?: string;
      sharingWarning?: string;
    }>('/api/slip/upload', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  deleteSlip: (payload: { slipUrl: string }) =>
    request<{ success: boolean }>('/api/slip/delete', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getLogs: (limit = 200) =>
    request<{ logs: LogEntry[] }>('/api/logs' + withQuery({ limit })),

  clearLogs: () => request<{ success: boolean }>('/api/logs', { method: 'DELETE' }),
};

export interface LogEntry {
  timestamp: string;
  level: 'error' | 'warn' | 'info';
  source: string;
  message: string;
  detail: string;
}
